import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import cx from 'classnames';

import LoadingBar from '../../ui/loading-bar';
import IconCheck from '../../ui/icons/check';
import Button from '../../ui/button';
import IconACTWhite2 from '../../ui/icons/act-white-2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import addToCartFetch from '../../../services/cart/add-to-cart';
import {
  addedProduct,
  addToCartAtom,
  cartAtom,
  openCartToggle,
} from '../../../state/cart';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Cookies from 'js-cookie';
import { getCartKeys } from '../../../utils/react-query/query-keys';
import { useAppContext } from '../../providers/app-context';
const PriceRequest = dynamic(() => import('./price-request'), { ssr: false });

const AddToCart: FunctionComponent<{
  productDetail: TProductDetail;
  actState?: 'ADDED' | undefined;
  isImageVisible?: boolean;
}> = ({ productDetail, actState, isImageVisible }) => {
  const { locale = 'en', pathname } = useRouter();
  const { detailedPrice } = useAppContext();
  const [, toggleCart] = useAtom(openCartToggle);
  const [, addToCart] = useAtom(addToCartAtom);
  const [, setAddedProduct] = useAtom(addedProduct);
  const [shoppingCart, setShoppingCart] = useAtom(cartAtom);
  const [showPriceRequestModal, setShowPriceRequestModal] =
    useState<boolean>(false);
  const inStock = productDetail.status === 'STO';
  const isReservated = productDetail.status === 'RSV';
  const { t } = useTranslation();
  const queryClient = useQueryClient()

  const { mutateAsync: addToCartMutate, isLoading: addingProduct } =
    useMutation(addToCartFetch, {
      onSuccess: (data) => {
        // update cartAtom to display the correct number of items on header
        // this will also call a useEffect on the product page see: shoppingCart
        addToCart(productDetail.id);
        setAddedProduct(productDetail.id);
        setShoppingCart(
          data.products.map((product) => {
            return product.id;
          })
        );
        queryClient.resetQueries(getCartKeys({ locale, isAdvancedView: false, detailedPrice: detailedPrice || false }));
        //queryClient.setQueryData(getCartKeys({ locale, isAdvancedView: false, detailedPrice: detailedPrice || false }), data);
        toggleCart();

        window.dataLayer.push({
          event: 'eec.add',
          ecommerce: {
            currencyCode: 'EUR', // currency code
            add: {
              products: [
                {
                  id: productDetail.sku, // SKU of the product
                  name: productDetail.part_en, // name of the product.
                  brand: productDetail.car.brand_name, // brand of the part
                  category: productDetail.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
                  price: Number(productDetail.pricing?.price_product).toFixed(
                    2
                  ), // price of the part without shipping, tax and currency
                  quantity: 1, // quantity added to the cart
                  dimension2: productDetail.reference, // reference of the product
                  dimension3: productDetail.car.model_name, // car model of the part
                  dimension4: productDetail.car?.version_name, // car version of the part
                },
              ],
            },
          },
        });

        // GA4
        window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'EUR',
            items: [
              {
                item_name: productDetail.part_en,
                item_id: productDetail.sku,
                price: Number(productDetail.pricing?.price_product).toFixed(2),
                item_brand: productDetail.car.brand_name,
                item_category: productDetail.category_en,
                item_category2: productDetail.part_en,
                //item_list_name: 'Search Results',
                //item_list_id: 'search-results',
                index: 0, // hardcoded to zero in this event
                quantity: '1',
              },
            ],
          },
        });
      },
    });

  const priceRequest = useCallback(async () => {
    setShowPriceRequestModal(true);
  }, [setShowPriceRequestModal]);

  return (
    <>
      <div
        className={cx(
          'act relative',
          'max-md:px-4',
          isImageVisible ? '' : 'lg:ml-[16px]'
        )}
      >
        {addingProduct && <LoadingBar />}
        {(isReservated || !inStock) && (
          <Button
            id="act_button"
            disabled={true}
            variant="CTA"
            data-cy="add-to-cart"
            className="group/act flex items-center justify-center flex-row gap-2"
          >
            {isReservated
              ? t('product:availability.RSV')
              : t('product:availability.SOL')}
          </Button>
        )}
        {inStock &&
          productDetail?.in_store &&
          (productDetail.in_cart || actState === 'ADDED' ? (
            <Button
              id="act_button"
              onClick={() => toggleCart()}
              variant="CTA"
              data-cy="add-to-cart"
              className="group/act flex items-center justify-center flex-row gap-2 "
            >
              <span className="group-hover/act:text-neutral-80">
                {t('product:open_cart')}
              </span>
              <IconCheck
                className={cx(
                  'h-4',
                  'w-4 text-white group-hover/act:text-neutral-80'
                )}
              ></IconCheck>
            </Button>
          ) : productDetail?.pricing?.price_final_printable ? (
            <Button
              id="act_button"
              onClick={() => {
                addToCartMutate({
                  locale,
                  product_id: productDetail.id,
                  csrftoken: Cookies.get('csrftoken') || '',
                  detail_view: Cookies.get('detail_prices') || 'false',
                });
              }}
              variant="CTA"
              data-cy="add-to-cart"
              className="group/act flex items-center justify-center flex-row gap-2"
            >
              <span className="act_button_label group-hover/act:text-neutral-80">
                {t('product:add_to_cart')}
              </span>
              {inStock && (
                <IconACTWhite2
                  className={cx(
                    'h-4',
                    'w-4 text-white iconv2 group-hover/act:text-neutral-80'
                  )}
                ></IconACTWhite2>
              )}
            </Button>
          ) : (
            <Button
              onClick={priceRequest}
              variant="PRIMARY"
              data-cy="ask-quote"
              className="group/act flex items-center justify-center flex-row gap-2"
            >
              <span className="act_button_label group-hover/act:text-neutral-80 font-bold">
                {t('product:ask_quote')}
              </span>
            </Button>
          ))}
      </div>

      {!productDetail?.pricing?.price_final_printable && (
        <PriceRequest
          product_id={productDetail.id}
          isOpen={showPriceRequestModal}
          onClose={setShowPriceRequestModal}
        />
      )}
    </>
  );
};

export default AddToCart;
